// The analysis in the header: queued (and how many are before it), running (how far, and how long is left) or its
// report ready to read. It used to be a strip across the middle of the dashboard; it is a chip on every page now,
// because an analysis takes minutes and the user does not wait on the home for it. Pure.

import { isRunning, jobPercent, jobTimeLeft } from "./diagnosisView.ts";
import { READY_SHOWN_DAYS } from "./firstAnalysis.ts";
import type { AnalysisStatus } from "./firstAnalysis.ts";
import { t } from "../i18n/common.ts";

export interface AnalysisBadge {
    state: "queued" | "running" | "ready";
    label: string;
    /** The second half: how many are ahead, or how long is left. */
    detail: string | null;
    percent: number | null;
}

/** How often the header asks again while an analysis is under way. */
export const BADGE_POLL_MS = 15_000;

/**
 * Only the analyses the user asked for, or the first one made for them (`report`): the night's upkeep and the
 * old games read again with new rules are not news. A report is announced as ready for a week, until it is opened.
 */
export function analysisBadge(status: AnalysisStatus | null, seenJob: number | null,
                              now: Date = new Date()): AnalysisBadge | null {
    const read = status as (AnalysisStatus & { report?: AnalysisStatus["job"] }) | null;
    // The last report, not the last job: the night's upkeep queued after it must not hide it
    const job = (read?.report ?? read?.job) as (AnalysisStatus["job"] & { queueAhead?: number | null }) | null | undefined;
    if (!job || job.kind !== "report") return null;
    if (isRunning(job)) {
        if (job.status === "queued") {
            const ahead = job.queueAhead;
            return { state: "queued", label: t("analysisQueued"), detail: ahead == null ? null : t("analysisAhead", ahead), percent: null };
        }
        const percent = jobPercent(job);
        const left = jobTimeLeft(job, now);
        return { state: "running", label: t("analysisRunning", percent), detail: left ? t("analysisLeft", left) : null, percent };
    }
    if (job.status !== "done" || job.id === seenJob || !job.finishedAt) return null;
    const age = now.getTime() - new Date(job.finishedAt).getTime();
    if (!(age >= 0 && age < READY_SHOWN_DAYS * 86_400_000)) return null;
    return { state: "ready", label: t("analysisReady"), detail: null, percent: null };
}
